import React from 'react';
import { Chip } from '@mui/material';
import dayjs from 'dayjs';
import { utcToDisplayDateAndTime, formatDateTimeWithTimezone } from '../../utils/timezoneUtils';

export const getLocalDateTimeStrings = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  const iso = local.toISOString();
  const [date, time] = iso.split('T');
  return {
    date,
    time: time.slice(0, 5) // HH:MM
  };
};

/**
 * @param {number} [seqNo=1]
 * @param {string} [displayTimezone] - When provided, "today" and current time are in this timezone (IANA). Otherwise uses browser local.
 */
export const createDefaultSectorForm = (seqNo = 1, displayTimezone) => {
  let date, time;
  if (displayTimezone) {
    const nowIso = new Date().toISOString();
    const { date: d, time: t } = utcToDisplayDateAndTime(nowIso, displayTimezone);
    date = d;
    time = t;
  } else {
    const local = getLocalDateTimeStrings();
    date = local.date;
    time = local.time;
  }
  return {
    seqNo,
    departure: {
      airport: '',
      date,
      time,
      fullName: '',
      coordinates: '',
      facilityType: ''
    },
    arrival: {
      airport: '',
      estimatedDate: date,
      estimatedTime: time,
      fullName: '',
      coordinates: '',
      facilityType: ''
    },
    wakeTurbulence: '',
    flightRules: '',
    flightType: 'CHARTER',
    charterServiceLabel: '',
    itineraryNotes: '',
    groundTransferNotes: '',
    departureGroundHandler: { contactPerson: '', contactNumber: '' },
    arrivalGroundHandler: { contactPerson: '', contactNumber: '' },
    departureTransport: { vehicleDetails: '' },
    arrivalTransport: { vehicleDetails: '' },
    remarks: ''
  };
};

export const FACILITY_TYPE_OPTIONS = [
  { value: 'Airport', label: 'Airport' },
  { value: 'Heliport', label: 'Heliport' },
  { value: 'Helipad', label: 'Helipad' },
  { value: 'Airstrip', label: 'Airstrip' },
  { value: 'Other', label: 'Other' }
];

export const CHARTER_SERVICE_LABEL_OPTIONS = [
  { value: 'Helicopter Charter', label: 'Helicopter Charter' },
  { value: 'Fixed-Wing Charter', label: 'Fixed-Wing Charter' },
  { value: 'Charter', label: 'Charter' }
];

export const PASSENGER_TYPE_OPTIONS = [
  { value: 'Adult', label: 'Adult' },
  { value: 'Child', label: 'Child' },
  { value: 'Infant', label: 'Infant' }
];

export const WAKE_TURBULENCE_OPTIONS = [
  { value: 'H-HEAVY', label: 'H - HEAVY' },
  { value: 'M-MEDIUM', label: 'M - MEDIUM' },
  { value: 'L-LIGHT', label: 'L - LIGHT' },
  { value: 'J-SUPER', label: 'J - SUPER' }
];

export const FLIGHT_RULES_OPTIONS = [
  { value: 'I-IFR', label: 'I - IFR' },
  { value: 'V-VFR', label: 'V - VFR' },
  { value: 'Y-IFR FIRST', label: 'Y - IFR FIRST' },
  { value: 'Z-VFR FIRST', label: 'Z - VFR FIRST' }
];

export const COMPLETED_TRIP_STATUSES = ['COMPLETED', 'CHARTER_COMPLETED'];
export const COMPLETED_SECTOR_STATUSES = ['COMPLETED', 'SECTOR_COMPLETED'];
export const ATC_CLEARED_SECTOR_STATUSES = ['CLEARED', 'ATC_PASSED', 'COMPLETED', 'SECTOR_COMPLETED'];
export const SECTOR_CORRECTION_ROLES = [
  'SALES',
  'OPERATIONS_MANAGER',
  'OPERATIONS_EXECUTIVE',
  'ACCOUNTABLE_MANAGER',
  'SUPER_ADMIN'
];

/** Edit allowed on completed sectors/charters for Sales/Ops/AM/SA; AM/SA can also override ATC-cleared. */
export const canEditSectorAction = (sector, trip, user) => {
  if (['CANCELLED', 'GROUNDED'].includes(trip?.status)) return false;
  if (sector?.status === 'CANCELLED') return false;
  const atcCleared = ATC_CLEARED_SECTOR_STATUSES.includes(sector?.status);
  const canOverride = ['ACCOUNTABLE_MANAGER', 'SUPER_ADMIN'].includes(user?.role);
  const canCorrect = SECTOR_CORRECTION_ROLES.includes(user?.role) && (
    COMPLETED_TRIP_STATUSES.includes(trip?.status)
    || COMPLETED_SECTOR_STATUSES.includes(sector?.status)
  );
  return !atcCleared || canOverride || canCorrect;
};

/** Cancel allowed including completed sectors/charters for Sales/Ops/AM/SA. */
export const canCancelSectorAction = (sector, trip, user) => {
  if (['CANCELLED', 'GROUNDED'].includes(trip?.status)) return false;
  if (sector?.status === 'CANCELLED') return false;
  if (
    COMPLETED_SECTOR_STATUSES.includes(sector?.status)
    || COMPLETED_TRIP_STATUSES.includes(trip?.status)
  ) {
    return SECTOR_CORRECTION_ROLES.includes(user?.role);
  }
  return true;
};

/** Parse date and time (YYYY-MM-DD, HH:MM) to a comparable timestamp. Uses 00:00 when time is missing. */
export function toDateTimeMs(dateStr, timeStr) {
  if (!dateStr || !dateStr.trim()) return null;
  const date = typeof dateStr === 'string' ? dateStr.trim().slice(0, 10) : null;
  if (!date) return null;
  const time = (timeStr && typeof timeStr === 'string' && timeStr.trim()) ? timeStr.trim().slice(0, 5) : '00:00';
  const parsed = new Date(date + 'T' + time);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

/** Parse a sector's date (Date or ISO string) + time string to timestamp. */
export function sectorDateTimeToMs(dateVal, timeStr) {
  if (dateVal == null) return null;
  if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal.trim())) {
    return toDateTimeMs(dateVal, timeStr);
  }
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return null;
  const dateStr = d.toISOString().slice(0, 10);
  const time = (timeStr && String(timeStr).trim()) ? String(timeStr).trim().slice(0, 5) : '00:00';
  const parsed = new Date(dateStr + 'T' + time);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
}

/** Advisory threshold for long charter / sector schedules (hours). Not a hard block. */
export const LONG_CHARTER_HOURS = 8;

export function hoursBetweenMs(startMs, endMs) {
  if (startMs == null || endMs == null) return null;
  const h = (endMs - startMs) / (1000 * 60 * 60);
  return h > 0 ? h : null;
}

export function formatDurationHours(hours) {
  if (hours == null || !Number.isFinite(hours)) return '';
  const totalMins = Math.round(hours * 60);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (m === 0) return `${h} hour${h === 1 ? '' : 's'}`;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}

export function getSectorScheduleMs(sector) {
  if (!sector) return { start: null, end: null };
  const start = sectorDateTimeToMs(sector.departure?.date, sector.departure?.time);
  const end = sectorDateTimeToMs(
    sector.arrival?.estimatedDate || sector.arrival?.date,
    sector.arrival?.estimatedTime || sector.arrival?.time
  );
  return { start, end };
}

/**
 * First departure → last arrival/end across sectors.
 * extraPoints: [{ start, end }] in ms (e.g. the sector currently being edited in the form).
 */
export function getCharterScheduleSpanHours(sectors = [], extraPoints = []) {
  let minStart = null;
  let maxEnd = null;
  const consider = (start, end) => {
    if (start != null && (minStart == null || start < minStart)) minStart = start;
    const e = end != null ? end : start;
    if (e != null && (maxEnd == null || e > maxEnd)) maxEnd = e;
  };
  for (const s of sectors || []) {
    const { start, end } = getSectorScheduleMs(s);
    consider(start, end);
  }
  for (const p of extraPoints || []) {
    consider(p?.start, p?.end);
  }
  return hoursBetweenMs(minStart, maxEnd);
}

export function isLongerThanHours(hours, limit = LONG_CHARTER_HOURS) {
  return hours != null && Number.isFinite(hours) && hours > limit;
}

/**
 * Get the cutoff timestamp for the next sector: must be on or after this time.
 * Uses previous sector's estimated arrival if set; otherwise previous sector's departure.
 */
export function getPreviousSectorCutoffMs(lastSector) {
  if (!lastSector) return null;
  const hasArrival = lastSector.arrival?.estimatedDate != null;
  if (hasArrival) {
    const ms = sectorDateTimeToMs(lastSector.arrival.estimatedDate, lastSector.arrival.estimatedTime);
    if (ms != null) return ms;
  }
  if (lastSector.departure?.date != null)
    return sectorDateTimeToMs(lastSector.departure.date, lastSector.departure.time);
  return null;
}

/**
 * Format previous sector cutoff for error messages.
 * @param {object} lastSector - Previous sector with departure/arrival date and time
 * @param {string} [displayTimezone] - When provided, format date/time in this timezone with label (e.g. IST (GMT+5:30))
 */
export function formatPrevCutoffLabel(lastSector, displayTimezone) {
  if (!lastSector) return '';
  const hasArrival = lastSector.arrival?.estimatedDate != null;
  const dateVal = hasArrival ? lastSector.arrival.estimatedDate : lastSector.departure?.date;
  const timeStr = hasArrival ? lastSector.arrival?.estimatedTime : lastSector.departure?.time;
  if (dateVal == null) return '';
  const suffix = hasArrival ? 'estimated arrival' : 'departure';
  if (displayTimezone) {
    const formatted = formatDateTimeWithTimezone(dateVal, timeStr, displayTimezone);
    return formatted ? `${formatted} (previous sector ${suffix})` : '';
  }
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return '';
  const dateLabel = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const timeLabel = (timeStr && String(timeStr).trim()) ? String(timeStr).trim().slice(0, 5) : '';
  return timeLabel ? `${dateLabel}, ${timeLabel} (previous sector ${suffix})` : `${dateLabel} (previous sector ${suffix})`;
}

export const formatRoleLabel = (role = '') =>
  role.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

export const formatActorName = (actor) => {
  if (!actor) return 'System';
  if (typeof actor === 'string') return 'Unknown User';
  const name = `${actor.firstName || ''} ${actor.lastName || ''}`.trim();
  return name || actor.email || 'Unknown User';
};

export const formatStatusHistoryDateTime = (date) => {
  if (!date) return 'N/A';
  const d = new Date(date);
  return d.toLocaleString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

export const formatDateTime = (date, time) => {
  if (!date && !time) return '—';
  let formattedDate = '';
  if (date) {
    const parsed = dayjs(date);
    formattedDate = parsed.isValid() ? parsed.format('DD MMM YYYY') : date;
  }
  return `${formattedDate} ${time || ''}`.trim() || '—';
};

export const getAircraftLabel = (aircraftId, aircraftList = []) => {
  if (!aircraftId) return 'Unassigned';
  const id = typeof aircraftId === 'string' ? aircraftId : aircraftId._id;
  const match = aircraftList.find((ac) => ac._id === id);
  if (match) {
    return `${match.registration || match.tailNumber || 'Aircraft'} (${match.manufacturer || ''} ${match.model || ''})`.trim();
  }
  if (typeof aircraftId === 'object') {
    return (
      aircraftId.registration ||
      aircraftId.tailNumber ||
      aircraftId.manufacturer ||
      aircraftId.model ||
      aircraftId.name ||
      aircraftId._id ||
      'Assigned'
    );
  }
  return aircraftId;
};

export const getPilotNames = (assignedPilots = []) => {
  if (!assignedPilots.length) return 'Unassigned';
  return assignedPilots
    .map((pilot) => {
      if (!pilot) return '';
      if (typeof pilot === 'object') {
        const name = `${pilot.firstName || ''} ${pilot.lastName || ''}`.trim();
        if (name) return name;
        if (pilot.email) return pilot.email;
        if (pilot._id) return pilot._id;
      }
      return pilot;
    })
    .filter(Boolean)
    .join(', ');
};

export const renderApprovalChips = (pendingApprovals = [], approvalStatus = {}, keyPrefix = 'approval') => {
  const chips = pendingApprovals.map((approval, idx) => (
    <Chip
      key={`${keyPrefix}-pending-${approval.role}-${idx}`}
      label={`Pending ${formatRoleLabel(approval.role)}`}
      size="small"
      color="warning"
      variant="outlined"
    />
  ));

  if (approvalStatus?.amApproved) {
    chips.push(
      <Chip
        key={`${keyPrefix}-am-approved`}
        label={
          approvalStatus.amApprovedByName
            ? `Accountable Manager Approved by ${approvalStatus.amApprovedByName}`
            : 'Accountable Manager Approved'
        }
        size="small"
        color="success"
        variant="outlined"
      />
    );
  }

  if (approvalStatus?.camoApproved) {
    chips.push(
      <Chip
        key={`${keyPrefix}-camo-approved`}
        label={
          approvalStatus.camoApprovedByName
            ? `CAMO Approved by ${approvalStatus.camoApprovedByName}`
            : 'CAMO Approved'
        }
        size="small"
        color="success"
        variant="outlined"
      />
    );
  }

  if (approvalStatus?.allApproved && !pendingApprovals.length) {
    chips.push(
      <Chip
        key={`${keyPrefix}-all-approved`}
        label="All Approvals Complete"
        size="small"
        color="success"
      />
    );
  }

  return chips;
};

export const renderSectorRemarks = (sector = {}) => {
  // Use remarksHistory (required field)
  if (sector.remarksHistory && Array.isArray(sector.remarksHistory) && sector.remarksHistory.length > 0) {
    return sector.remarksHistory.map(remark => remark.text);
  }
  return [];
};

export const formatRoute = (sector) => {
  const depAirport = sector.departure?.airport || 'N/A';
  let arrAirport = sector.arrival?.airport || 'N/A';
  
  // Validate arrival airport - check if it looks like a time (contains ":" and matches time pattern)
  if (arrAirport && /^\d{1,2}:\d{2}$/.test(arrAirport.trim())) {
    // This is likely a time value incorrectly stored in arrival.airport
    arrAirport = 'N/A (Invalid Data)';
  }
  
  return `${depAirport} → ${arrAirport}`;
};

export const buildErrorMessage = (action, error) => {
  const errorDetails = error.response?.data;
  
  if (errorDetails?.errors && Array.isArray(errorDetails.errors) && errorDetails.errors.length > 0) {
    const validationErrors = errorDetails.errors.map(err => {
      const field = err.param || err.path || 'field';
      const msg = err.msg || err.message || 'Invalid value';
      return `${field}: ${msg}`;
    }).join('; ');
    return `${action} failed - Validation errors: ${validationErrors}`;
  } else if (errorDetails?.message) {
    return `${action} failed: ${errorDetails.message}`;
  } else if (error.response?.status === 403) {
    return `${action} failed: You do not have permission to perform this action.`;
  } else if (error.response?.status === 401) {
    return `${action} failed: Your session has expired. Please log in again.`;
  } else if (error.response?.status >= 500) {
    return `${action} failed: Server error. Please try again later or contact support.`;
  } else {
    return `${action} failed: ${error.message || 'An unexpected error occurred'}`;
  }
};

export const getStatusColor = (status) => {
  const colors = {
    DRAFT: 'default',
    PENDING_OPS: 'warning',
    CREW_ASSIGNMENT: 'info',
    READY_FOR_BA: 'primary',
    READY_FOR_ATC: 'secondary',
    GROUNDED: 'error',
    COMPLETED: 'success',
    CANCELLED: 'error'
  };
  return colors[status] || 'default';
};
